import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import styles from './css/BloodPressure.module.css';
import newStyles from './css/HIV.module.css';
import { BookHeart, Send } from 'lucide-react';

const HIV = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    const [consentGiven, setConsentGiven] = useState(false);
    const [testResult, setTestResult] = useState('');
    const [preCounselingChecklist, setPreCounselingChecklist] = useState({
        explainedTestProcedure: false,
        discussedWindowPeriod: false,
        discussedImplications: false,
        discussedConfidentiality: false,
    });

    const [postCounselingNotes, setPostCounselingNotes] = useState('');

    const handleChecklistChange = (key) => {
        setPreCounselingChecklist((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                hiv_test: {
                    consentGiven,
                    testResult,
                    preCounselingChecklist,
                    postCounselingNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(styles.hideSidebar);
        return () => {
            document.body.classList.remove(styles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.bloodPressureContainer}>
            <Navbar />

            <main className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon} aria-hidden="true">
                            <BookHeart />
                        </div>
                        <h1 className={newStyles.title}>HIV Test</h1>
                    </div>

                    <div className={styles.patientCard}>
                        <div className={styles.avatar} aria-hidden="true">{initials}</div>
                        <div className={styles.patientText}>
                            <div className={styles.patientName}>{patientName}</div>
                            <div className={styles.patientMetaRow}>
                                <span className={styles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={styles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>CONSENT</div>
                        <div className={newStyles.consentCard}>
                            <div className={newStyles.consentRow}>
                                <Checkbox
                                    id="hivConsent"
                                    checked={consentGiven}
                                    onChange={() => setConsentGiven((v) => !v)}
                                />
                                <label htmlFor="hivConsent" className={newStyles.consentLabel}>
                                    Patient has given informed consent for HIV testing
                                </label>
                            </div>
                        </div>

                        {!consentGiven && (
                            <div className={newStyles.consentActions}>
                                <div className={newStyles.consentHint}>
                                    Consent required before proceeding with test.
                                </div>
                                <button
                                    type="button"
                                    className={newStyles.cancelButton}
                                    onClick={() => window.history.back()}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    {consentGiven && (
                        <>
                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionTitle}>RESULT</div>
                                <div className={newStyles.readingsRow}>
                                    <div className={newStyles.readingItem}>
                                        <label className={styles.readingLabel}>Test Result</label>
                                        <div className={styles.inputGroup}>
                                            <Dropdown
                                                name="hivTestResult"
                                                value={testResult}
                                                onChange={setTestResult}
                                                options={['Negative', 'Positive', 'Invalid']}
                                                placeholder="Select..."
                                                className={newStyles.readingInput}
                                                alignLeft
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionTitle}>PRE-COUNSELING CHECKLIST</div>

                                <div className={styles.symptomsList}>
                                    <div className={styles.checkboxItem}>
                                        <Checkbox
                                            id="explainedTestProcedure"
                                            checked={preCounselingChecklist.explainedTestProcedure}
                                            onChange={() => handleChecklistChange('explainedTestProcedure')}
                                        />
                                        <label htmlFor="explainedTestProcedure" className={styles.checkboxLabel}>Explained test procedure</label>
                                    </div>

                                    <div className={styles.checkboxItem}>
                                        <Checkbox
                                            id="discussedWindowPeriod"
                                            checked={preCounselingChecklist.discussedWindowPeriod}
                                            onChange={() => handleChecklistChange('discussedWindowPeriod')}
                                        />
                                        <label htmlFor="discussedWindowPeriod" className={styles.checkboxLabel}>Discussed window period</label>
                                    </div>

                                    <div className={styles.checkboxItem}>
                                        <Checkbox
                                            id="discussedImplications"
                                            checked={preCounselingChecklist.discussedImplications}
                                            onChange={() => handleChecklistChange('discussedImplications')}
                                        />
                                        <label htmlFor="discussedImplications" className={styles.checkboxLabel}>Discussed implications of results</label>
                                    </div>

                                    <div className={styles.checkboxItem}>
                                        <Checkbox
                                            id="discussedConfidentiality"
                                            checked={preCounselingChecklist.discussedConfidentiality}
                                            onChange={() => handleChecklistChange('discussedConfidentiality')}
                                        />
                                        <label htmlFor="discussedConfidentiality" className={styles.checkboxLabel}>Discussed confidentiality</label>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.sectionBlock}>
                                <div className={styles.fieldLabel}>Post-Counseling Notes</div>
                                <textarea
                                    value={postCounselingNotes}
                                    onChange={(e) => setPostCounselingNotes(e.target.value)}
                                    className={styles.clinicalNotes}
                                    placeholder="Enter clinical notes..."
                                    rows={6}
                                />
                            </div>

                            <div className={styles.cardFooter}>
                                <Button
                                    onClick={handleInvoice}
                                    className={styles.invoiceButton}
                                    type="button"
                                >
                                    <Send size={16} />
                                    Invoice
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default HIV;